import facebookAuth from './facebookAuth';

const API_URL = process.env.REACT_APP_API_URL || 'https://toldfel.hu';

const facebookGraphAPI = {
  /**
   * Get the authenticated user's personal profile info (id, name)
   * Proxied through backend to avoid browser tracking prevention.
   */
  async getMe() {
    const token = facebookAuth.getToken();
    if (!token || !token.access_token) return null;
    // Return cached name from stored token if available (no extra request needed)
    if (token.user_name) return { name: token.user_name };
    try {
      const response = await fetch(`${API_URL}/api/facebook/me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token.access_token })
      });
      const data = await response.json();
      if (data.error) return null;
      // Cache the name in localStorage token for next time
      if (data.name) {
        try {
          const stored = JSON.parse(localStorage.getItem('facebook_token') || '{}');
          stored.user_name = data.name;
          localStorage.setItem('facebook_token', JSON.stringify(stored));
        } catch (e) {}
      }
      return data;
    } catch (e) {
      return null;
    }
  },

  /**
   * Get user's Facebook Pages
   * Proxied through backend to avoid browser tracking prevention.
   */
  async getPages() {
    const token = facebookAuth.getToken();
    if (!token || !token.access_token) {
      throw new Error('Not authenticated with Facebook');
    }

    // Check if token is expired
    if (facebookAuth.isTokenExpired()) {
      console.warn('Facebook token has expired');
      throw new Error('Facebook token has expired. Please reconnect your Facebook account.');
    }

    console.log('Fetching Facebook Pages via backend proxy...');

    const response = await fetch(`${API_URL}/api/facebook/pages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: token.access_token })
    });
    const data = await response.json();

    if (data.error) {
      console.error('Facebook Pages proxy error:', data.error);
      throw new Error(data.error || 'Failed to get Facebook Pages');
    }

    const pages = data.pages || [];
    console.log('Number of pages found:', pages.length);
    pages.forEach((page, index) => {
      console.log(`Page ${index + 1}:`, { id: page.id, name: page.name, hasInstagram: !!page.instagram_business_account });
    });

    return pages;
  },

  /**
   * Get Instagram Business Account connected to a Page
   * Proxied through backend to avoid browser tracking prevention.
   */
  async getInstagramAccount(pageId, pageAccessToken) {
    const response = await fetch(`${API_URL}/api/facebook/instagram-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pageId, pageAccessToken })
    });
    const data = await response.json();

    if (data.error) {
      throw new Error(data.error || 'Failed to get Instagram account');
    }

    return data.instagram_business_account || null;
  },

  /**
   * Upload Reel to Instagram using resumable upload
   * All calls are proxied through backend to avoid browser tracking prevention.
   * This is a multi-step process:
   * 1. Initialize upload session (create media container)
   * 2. Poll for upload status
   * 3. Publish the reel
   */
  async uploadInstagramReel(igUserId, pageAccessToken, videoUrl, caption) {
    try {
      // Step 1: Create container (initialize upload)
      const initResponse = await fetch(`${API_URL}/api/facebook/instagram-create-media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ igUserId, pageAccessToken, videoUrl, caption })
      });

      const initData = await initResponse.json();

      if (!initResponse.ok || initData.error) {
        throw new Error(initData.error || 'Failed to create media container');
      }

      const creationId = initData.creation_id;

      // Step 2: Poll for upload status (client-side polling via backend proxy)
      let status = 'IN_PROGRESS';
      let attempts = 0;
      const maxAttempts = 60; // 5 minutes max

      while (status === 'IN_PROGRESS' && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds

        const statusResponse = await fetch(`${API_URL}/api/facebook/instagram-media-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ creationId, pageAccessToken })
        });
        const statusData = await statusResponse.json();

        if (!statusResponse.ok || statusData.error) {
          throw new Error(statusData.error || 'Failed to check upload status');
        }

        status = statusData.status_code;
        attempts++;
      }

      if (status !== 'FINISHED') {
        throw new Error('Video upload timed out or failed (status: ' + status + ')');
      }

      // Step 3: Publish the reel
      const publishResponse = await fetch(`${API_URL}/api/facebook/instagram-publish-media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ igUserId, pageAccessToken, creationId })
      });

      const publishData = await publishResponse.json();

      if (!publishResponse.ok || publishData.error) {
        throw new Error(publishData.error || 'Failed to publish reel');
      }

      return {
        success: true,
        id: publishData.id,
        message: 'Reel published successfully'
      };

    } catch (error) {
      console.error('Instagram Reel upload error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Upload video to Facebook Page as Reel
   * Uses the public video URL from backend, proxied through backend to avoid
   * browser tracking prevention blocking direct graph.facebook.com requests.
   */
  async uploadFacebookReel(pageId, pageAccessToken, videoUrl, title, description) {
    try {
      console.log('Uploading Facebook Reel:', { pageId, videoUrl, title });

      const response = await fetch(`${API_URL}/api/facebook/upload-reel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageId, pageAccessToken, videoUrl, title, description: description || '' })
      });

      const data = await response.json();

      console.log('Facebook upload response:', data);

      if (!response.ok || data.error) {
        console.error('Facebook upload error:', data.error);
        throw new Error(data.error || 'Failed to upload Facebook video');
      }

      return {
        success: true,
        id: data.id,
        message: 'Video uploaded to Facebook Page successfully'
      };

    } catch (error) {
      console.error('Facebook video upload error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Helper to upload video file to a temporary public URL
   * (You'll need a backend service for this or use a temporary storage service)
   */
  async uploadVideoToPublicUrl(videoFile) {
    // TODO: Implement backend endpoint to:
    // 1. Receive video file
    // 2. Store it temporarily (S3, Cloudinary, etc.)
    // 3. Return a public URL
    // For now, return error
    throw new Error('Backend video upload endpoint not implemented. You need to upload the video to a publicly accessible URL first.');
  }
};

export default facebookGraphAPI;
